import React, { useRef, useMemo, useState, useEffect, useCallback } from "react";
import MortgageQualifier from "../components/MortgageQualifier";
import StickyMobileCTA from "../components/StickyMobileCTA";
import HeroBackdrop from "../components/HeroBackdrop";
import FaqItem from "../components/FaqItem";
import useReveal from "../hooks/useReveal";
import { getHook } from "../lib/tracking";

/**
 * Message match: the DomainEx tracking link carries a `hook` (the ad's angle,
 * set per link or overridden with ?hook= on the ad URL). The headline echoes
 * that angle so the click lands on the promise the ad made. Unknown or
 * missing hooks fall back to the default. Same page, same form, same single
 * action, only the headline pair changes. Add a hook here when you launch a
 * new ad angle; keep keys lowercase.
 */
const HEADLINES = {
  default:         { lead: "Find out what you can", em: "actually get approved for." },
  declined:        { lead: "Turned down by a bank?", em: "That's not the final answer." },
  "self-employed": { lead: "Self-employed? Find out what", em: "lenders will actually approve." },
  "first-time":    { lead: "Buying your first home? See what", em: "you can actually get approved for." },
  "down-payment":  { lead: "Not sure your down payment", em: "is enough? Find out in 90 seconds." },
  // Matches the CreditScore ad angle (the best performer: ~73% of spend, lowest CPA).
  // DRAFT COPY - confirm wording with Loic before it carries paid traffic.
  "credit-score":  { lead: "Credit score holding you back?", em: "See what lenders will actually approve." },
};
HEADLINES.rejected = HEADLINES.declined;
HEADLINES.selfemployed = HEADLINES["self-employed"];
HEADLINES.firsttime = HEADLINES["first-time"];
HEADLINES.creditscore = HEADLINES["credit-score"];

function useHeadline() {
  return useMemo(() => {
    let h = null;
    try {
      // Read the URL directly on first paint (attribution capture runs in an
      // effect, after this render), falling back to the stored first touch.
      h = new URLSearchParams(window.location.search).get("hook");
    } catch { /* ignore */ }
    h = (h && /^[a-z0-9_-]{1,64}$/i.test(h) ? h.toLowerCase() : null) || getHook();
    return HEADLINES[h] || HEADLINES.default;
  }, []);
}

// The three reassurance blocks, now presented as expandable FAQ rows below
// the hero. Same copy that was previously the trust strip.
const FAQS = [
  {
    id: "why-ask-first",
    q: "Why we ask first",
    a: "A bank pre-approves you off a template. Lender criteria shift constantly, and I track them so I can start from your actual situation instead of a generic form.",
  },
  {
    id: "who-reads-it",
    q: "Who reads it",
    a: "I do. Dorian Reeves, licensed mortgage associate with MMG Mortgages. Same person every time. No call centre, no handoff to a junior.",
  },
  {
    id: "if-not-ready",
    q: "If you're not ready",
    a: "You get a 90-day plan instead of a brush-off: the specific things to fix so that next time, you walk in already approved.",
  },
];

/**
 * The whole public site: one page, one audience (Alberta buyers), one
 * action (start the qualifier). Per the FE Lander Playbook, the form is
 * on screen at load, no hero CTA button that only scrolls, one reassurance
 * line at the commitment point, sticky CTA once the form scrolls out.
 */
export default function MortgageLander() {
  useReveal();
  const headline = useHeadline();
  const formRef = useRef(null);

  // Focus-on-engage: when the user starts filling the form, dim ambient
  // elements (eyebrow, H1, bio, FAQ, header, backdrop) so the form has
  // full attention. Restore on form completion. Toggled via `body.is-focused`
  // so the header (rendered by SiteShell) can be dimmed from here too.
  const [focused, setFocused] = useState(false);
  const engage = useCallback(() => setFocused(true), []);
  const release = useCallback(() => setFocused(false), []);
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("is-focused", focused);
    return () => document.body.classList.remove("is-focused");
  }, [focused]);

  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      {/* HERO - H1 on the left, form on the right, house backdrop behind.
          Bio band (paragraph + Dorian headshot) lives below the grid,
          inside the same section so the backdrop washes behind it too. */}
      <section
        id="top"
        className="z-section hero-section"
        data-testid="mortgage-hero"
        data-track-section="mortgage_hero"
        data-track-as="ViewContent"
      >
        <HeroBackdrop />
        <div className="z-container w-full" style={{ position: "relative", zIndex: 1 }}>
          <div className="hero-stack">
            {/* Eyebrow + H1 stack directly above the form */}
            <div className="hero-lede">
              <div className="z-eyebrow" data-testid="mortgage-eyebrow">
                Alberta Only · Real Person Reads Every Answer
              </div>
              <h1
                className="mortgage-h1"
                data-testid="mortgage-h1"
              >
                {headline.lead}<br />
                <em style={{ color: "var(--accent)", fontStyle: "italic" }}>
                  {headline.em}
                </em>
              </h1>
            </div>

            {/* Pre-form trust card - human reassurance right before the ask.
                Fades with the rest of the ambient surfaces the moment the
                visitor picks their first option (see body.is-focused). */}
            <div className="trust-mini" data-testid="mortgage-trust-mini">
              <div className="bio-row">
                <div className="bio-avatar">
                  <img src="/assets/dorian-headshot-144.webp" alt="Dorian Reeves" width="72" height="72" />
                </div>
                <div className="bio-name">
                  <b>Dorian Reeves</b>
                  <span>Licensed Mortgage Associate · MMG Mortgages</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <div ref={formRef} id="apply" style={{ scrollMarginTop: "calc(var(--header-h) + 16px)" }}>
              <div className="hero-form-card" data-track-section="mortgage_form" data-track-as="ViewContent">
                <MortgageQualifier
                  source="mortgages"
                  formName="mq-6step"
                  onEngage={engage}
                  onDone={release}
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ - replaces the old 3-column trust strip. Click to toggle,
          hover-preview on desktop pointer devices. */}
      <section
        className="z-section"
        style={{ paddingTop: 8, paddingBottom: 48 }}
        data-testid="mortgage-faq"
        data-track-section="mortgage_faq"
      >
        <div className="z-container">
          <div className="faq-list reveal">
            {FAQS.map((f) => (
              <FaqItem key={f.id} id={f.id} question={f.q}>
                <p>{f.a}</p>
              </FaqItem>
            ))}
          </div>
          <p style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.55, marginTop: 40, maxWidth: 640, textAlign: "center", marginLeft: "auto", marginRight: "auto" }}>
            <b style={{ color: "var(--text-secondary)" }}>MMG Mortgages</b> · Dorian Reeves, licensed mortgage
            associate. Serving Alberta. Rates and approvals vary by lender and are subject to credit approval and
            property qualification. A pre-approval does not guarantee final financing. <a href="/privacy" style={{ color: "var(--text-secondary)" }}>Privacy Policy</a>.
          </p>
        </div>
      </section>

      <StickyMobileCTA
        triggerRef={formRef}
        label="Start my buyer profile"
        onClick={scrollToForm}
        testId="mortgage-sticky-cta"
      />
    </>
  );
}
